import React, { useEffect, useState } from "react";
import { Button, Table, Modal, Spinner, Form, FloatingLabel, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faDownload } from "@fortawesome/free-solid-svg-icons";
import buttonStyles from "../../styles/Buttons.module.css";
import styles from "../../styles/Modal.module.css";

// Components
import ConfirmModal from "../../components/ConfirmModal";
import AlertModal from "../../components/AlertModal";
import AppNav from "../../header/AppNav";
import { AppPagination } from "../components/Index";

// Services
import { AshaBlockServices } from "../../services/AshaBlocks";
import { AshaServices } from "../../services/AshaService";
import { AuthService } from "../../services/AuthService";
import { AshaAbstractReportService } from "../../services/AshaAbstractReportService";
import * as XLSX from 'xlsx';

function AshaAbstractReport() {
  // State for data and loading
  const [abstractReports, setAbstractReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [deleteData, setDeleteData] = useState({ id: null, name: '' });
  
  // State for form data
  const [formData, setFormData] = useState({
    id: '',
    ashaBlockId: '',
    ashaId: '',
    status: 'Active',
    totalHouses: '',
    housesScreened: '',
    totalPopulation: '',
    personsScreened: '',
    personsAbove40Screened: '',
    personsWithDiabetesScreened: '',
    personsReferredToSecondaryCamp: ''
  });
  const [errors, setErrors] = useState({});

  // State for dropdowns
  const [ashaBlocks, setAshaBlocks] = useState([]);
  const [ashas, setAshas] = useState([]);
  const [selectedAshaBlock, setSelectedAshaBlock] = useState("");
  const [selectedAsha, setSelectedAsha] = useState("");
  
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;
  
  // User role and authentication
  const roleDetails = AuthService.getUserRole();
  const userRole = roleDetails?.role;
  const user = AuthService.getAuthEntity();

  // Check if user is PHC role
  const isPhcRole = userRole === "PHC";

  // Redirect if not PHC role
  useEffect(() => {
    if (!isPhcRole) {
      setModalData({
        title: 'Access Denied',
        message: 'This page is only accessible to PHC users.',
        variant: 'danger'
      });
      setShowAlertModal(true);
      return;
    }
  }, [isPhcRole]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Fetch data on component mount
  useEffect(() => {
    if (isPhcRole) {
      const initialize = async () => {
        await initializeData();
        await fetchAbstractReports();
      };
      initialize();
    }
  }, [isPhcRole]);

  const initializeData = async () => {
    try {
      if (isPhcRole) {
        const phcId = user.phcDetails?.id;
        await loadAshaBlocksForPhc(phcId);
        await loadAshasForPhc(phcId);
      }
    } catch (error) {
      console.error("Error initializing data:", error);
      setModalData({
        title: 'Error',
        message: 'Failed to load dropdown data. Please refresh the page.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    }
  };
  
  const loadAshaBlocksForPhc = async (phcId) => {
    try {
      const blocksRes = await AshaBlockServices.geAshaBlockByPhcId(phcId);
      setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
    } catch (error) {
      console.error("Error loading Asha Blocks:", error);
    }
  };
  
  const loadAshasForPhc = async (phcId) => {
    try {
      const ashasRes = await AshaServices.getAshaByPhcId(phcId);
      setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
    } catch (error) {
      console.error("Error loading Ashas:", error);
    }
  };

  const loadAshasForAshaBlock = async (ashaBlockId) => {
    try {
      const ashasRes = await AshaServices.getAshaByAshaBlockId(ashaBlockId);
      setAshas(Array.isArray(ashasRes) ? ashasRes : Object.values(ashasRes || {}));
    } catch (error) {
      console.error("Error loading Ashas for Asha Block:", error);
      setAshas([]);
    }
  };

  // Handle Asha Block change
  const handleAshaBlockChange = async (e) => {
    const value = e.target.value;
    setSelectedAshaBlock(value);
    setSelectedAsha("");
    
    if (value) {
      await loadAshasForAshaBlock(value);
    } else {
      // Load all Ashas for PHC when no block is selected
      const phcId = user.phcDetails?.id;
      await loadAshasForPhc(phcId);
    }
  };

  const fetchAbstractReports = async () => {
    try {
      setLoading(true);
      let reports = [];
      
      if (isPhcRole) {
        const phcId = user.phcDetails?.id;
        if (phcId) {
          reports = await AshaAbstractReportService.getByPhcId(phcId);
        }
      }
      
      setAbstractReports(Array.isArray(reports) ? reports : []);
      setTotalElements(reports.length);
      setTotalPages(Math.ceil(reports.length / pageSize));
    } catch (err) {
      console.error("Error fetching abstract reports:", err);
      setError("Failed to load abstract reports. Please try again later.");
      setAbstractReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearModalFilters = () => {
    setFormData({
      id: '',
      ashaBlockId: '',
      ashaId: '',
      status: 'Active',
      totalHouses: '',
      housesScreened: '',
      totalPopulation: '',
      personsScreened: '',
      personsAbove40Screened: '',
      personsWithDiabetesScreened: '',
      personsReferredToSecondaryCamp: ''
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.ashaBlockId) {
      newErrors.ashaBlockId = 'Please select an Asha Block';
    }
    if (!formData.ashaId) {
      newErrors.ashaId = 'Please select an Asha';
    }
    if (!formData.totalHouses || formData.totalHouses === '') {
      newErrors.totalHouses = 'Please enter total number of houses';
    }
    if (!formData.housesScreened || formData.housesScreened === '') {
      newErrors.housesScreened = 'Please enter number of houses screened';
    }
    if (!formData.totalPopulation || formData.totalPopulation === '') {
      newErrors.totalPopulation = 'Please enter total population';
    }
    if (!formData.personsScreened || formData.personsScreened === '') {
      newErrors.personsScreened = 'Please enter number of persons screened';
    }
    if (!formData.personsAbove40Screened || formData.personsAbove40Screened === '') {
      newErrors.personsAbove40Screened = 'Please enter number of persons above 40 screened';
    }
    if (!formData.personsWithDiabetesScreened || formData.personsWithDiabetesScreened === '') {
      newErrors.personsWithDiabetesScreened = 'Please enter number of persons with diabetes screened';
    }
    if (!formData.personsReferredToSecondaryCamp || formData.personsReferredToSecondaryCamp === '') {
      newErrors.personsReferredToSecondaryCamp = 'Please enter number of persons referred to secondary camp';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditClick = (report) => {
    const ashaBlockId =
      report?.ashaDetails?.ashaBlockDetails?.id != null
        ? String(report.ashaDetails.ashaBlockDetails.id)
        : '';
    const ashaId =
      report?.ashaDetails?.id != null ? String(report.ashaDetails.id) : '';

    setFormData({
      id: report.id,
      ashaBlockId,
      ashaId,
      status: report.status || 'Active',
      totalHouses: report.totalHouses != null ? String(report.totalHouses) : '',
      housesScreened: report.housesScreened != null ? String(report.housesScreened) : '',
      totalPopulation: report.totalPopulation != null ? String(report.totalPopulation) : '',
      personsScreened: report.personsScreened != null ? String(report.personsScreened) : '',
      personsAbove40Screened: report.personsAbove40Screened != null ? String(report.personsAbove40Screened) : '',
      personsWithDiabetesScreened: report.personsWithDiabetesScreened != null ? String(report.personsWithDiabetesScreened) : '',
      personsReferredToSecondaryCamp: report.personsReferredToSecondaryCamp != null ? String(report.personsReferredToSecondaryCamp) : ''
    });

    if (ashaBlockId) {
      loadAshasForAshaBlock(ashaBlockId);
    }

    setShowEditModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare data for API submission
      const submitData = {
        ashaBlockId: parseInt(formData.ashaBlockId),
        ashaId: parseInt(formData.ashaId),
        totalHouses: parseInt(formData.totalHouses),
        housesScreened: parseInt(formData.housesScreened),
        totalPopulation: parseInt(formData.totalPopulation),
        personsScreened: parseInt(formData.personsScreened),
        personsAbove40Screened: parseInt(formData.personsAbove40Screened),
        personsWithDiabetesScreened: parseInt(formData.personsWithDiabetesScreened),
        personsReferredToSecondaryCamp: parseInt(formData.personsReferredToSecondaryCamp),
        status: formData.status
      };
      
      // Call actual API service
      const response = await AshaAbstractReportService.create(submitData);
      
      if (response.success || response.status == 200) {
        clearModalFilters();
        setShowAddModal(false);
        setModalData({
          title: 'Success',
          message: 'Abstract report added successfully!',
          variant: 'success'
        });
        setShowAlertModal(true);
        fetchAbstractReports();
      } else {
        setModalData({
          title: 'Error',
          message: error.message || 'Failed to add abstract report. Please try again.',
          variant: 'danger'
        });
        setShowAlertModal(true);
      }
    } catch (error) {
      console.error('Error adding abstract report:', error);
      setModalData({
        title: 'Error',
        message: error.message || 'Failed to add abstract report. Please try again.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = {
        ashaBlockId: parseInt(formData.ashaBlockId),
        ashaId: parseInt(formData.ashaId),
        totalHouses: parseInt(formData.totalHouses),
        housesScreened: parseInt(formData.housesScreened),
        totalPopulation: parseInt(formData.totalPopulation),
        personsScreened: parseInt(formData.personsScreened),
        personsAbove40Screened: parseInt(formData.personsAbove40Screened),
        personsWithDiabetesScreened: parseInt(formData.personsWithDiabetesScreened),
        personsReferredToSecondaryCamp: parseInt(formData.personsReferredToSecondaryCamp),
        status: formData.status,
        id: formData.id
      };
      
      const response = await AshaAbstractReportService.update(submitData);
      
      if (response.success || response.status === 200) {
        clearModalFilters();
        setShowEditModal(false);
        setModalData({
          title: 'Success',
          message: 'Abstract report updated successfully!',
          variant: 'success'
        });
        setShowAlertModal(true);
        fetchAbstractReports();
      } else {
        setModalData({
          title: 'Error',
          message: 'Failed to update abstract report. Please try again.',
          variant: 'danger'
        });
        setShowAlertModal(true);
      }
    } catch (error) {
      console.error('Error updating abstract report:', error);
      setModalData({
        title: 'Error',
        message: error.message || 'Failed to update abstract report. Please try again.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Filter reports based on search term and selected filters
  const filteredReports = abstractReports.filter((report) => {
    // Search functionality
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        (report.ashaBlockDetails?.ashaBlock || "").toLowerCase().includes(searchLower) ||
        (report.ashaDetails?.name || "").toLowerCase().includes(searchLower) ||
        (report.reportType || "").toLowerCase().includes(searchLower) ||
        (report.description || "").toLowerCase().includes(searchLower) ||
        new Date(report.reportDate).toLocaleDateString('en-GB').toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }
    
    // Filter by selected Asha Block
    if (selectedAshaBlock) {
      if (!report.ashaBlockDetails || report.ashaBlockDetails.id != selectedAshaBlock) {
        return false;
      }
    }
    
    // Filter by selected Asha
    if (selectedAsha) {
      if (!report.ashaDetails || report.ashaDetails.id != selectedAsha) {
        return false;
      }
    }
    
    return true;
  });

  const handlePageChange = (page) => {
    setCurrentPage(page - 1);
  };

  // Don't render anything if not PHC role
  if (!isPhcRole) {
    return (
      <div>
        <AppNav />
        <AlertModal
          show={showAlertModal}
          onHide={() => setShowAlertModal(false)}
          title={modalData.title}
          message={modalData.message}
          variant={modalData.variant || 'info'}
        />
      </div>
    );
  }

  return (
    <div>
      <AppNav />
      <h1 className="list-style">Asha Abstract Report</h1>
      <div className="list-table-style">
        
        {/* Filters */}
        <Row className="mb-3">
          <Col>
            <FloatingLabel controlId="ashaBlock" label="Asha Block" className="mt-3">
              <Form.Select
                value={selectedAshaBlock}
                onChange={handleAshaBlockChange}
              >
                <option value="">All Asha Blocks</option>
                {ashaBlocks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.ashaBlock}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
          <Col>
            <FloatingLabel controlId="asha" label="Ashas" className="mt-3">
              <Form.Select
                value={selectedAsha}
                onChange={(e) => setSelectedAsha(e.target.value)}
              >
                <option value="">All Ashas</option>
                {ashas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
          <Col>
            <Form.Control
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mt-3"
            />
          </Col>
          <Col className="d-flex align-items-center justify-content-end mt-3">
            <Button 
              variant="primary"
              onClick={() => {
                clearModalFilters();
                setShowAddModal(true);
              }}
              className={buttonStyles.primaryButton}
            >
              Add
            </Button>
          </Col>
        </Row>
        
        {/* Main Content */}
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading Abstract Reports...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">{error}</div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="align-middle">
              {/* Table Header */}
              <thead className="table-light">
                <tr>
                  <th>SL No</th>
                  <th>Asha Block</th>
                  <th>Asha Name</th>
                  <th>Total Houses</th>
                  <th>Houses Screened</th>
                  <th>Total Population</th>
                  <th>Persons Screened</th>
                  <th>Above 40 Screened</th>
                  <th>Diabetes Screened</th>
                  <th>Referred to Camp</th>
                  <th>Actions</th>
                </tr>
              </thead>
              {/* Table Body */}
              <tbody>
                {filteredReports.map((report, index) => (
                  <tr key={report.id}>
                    <td>{currentPage * pageSize + index + 1}</td>
                    <td>{report.ashaDetails?.ashaBlockDetails?.ashaBlock || 'N/A'}</td>
                    <td>{report.ashaDetails?.name || 'N/A'}</td>
                    <td>{report.totalHouses || 0}</td>
                    <td>{report.housesScreened || 0}</td>
                    <td>{report.totalPopulation || 0}</td>
                    <td>{report.personsScreened || 0}</td>
                    <td>{report.personsAbove40Screened || 0}</td>
                    <td>{report.personsWithDiabetesScreened || 0}</td>
                    <td>{report.personsReferredToSecondaryCamp || 0}</td>
                    {/* <Button
                        variant="link"
                        size="sm"
                        onClick={() => console.log('View report:', report)}
                        style={{ boxShadow: 'none' }}
                        className='text-muted me-2'
                      >
                        <FontAwesomeIcon icon={faEye} style={{ color: '#05275a' }} />
                      </Button> */}
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleEditClick(report)}
                        style={{ boxShadow: 'none' }}
                        className='text-muted'
                        title="Edit Report"
                      >
                        <FontAwesomeIcon icon={faEdit} style={{ color: '#05275a' }} />
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredReports.length === 0 && (
                  <tr>
                    <td colSpan="13" className="text-center p-4">
                      {searchTerm ? 'No reports found matching your search.' : 'No abstract reports found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center my-3">
            <AppPagination
              curPage={currentPage + 1} 
              maxPage={totalPages}
              changePage={handlePageChange}
            />
          </div>
        )}
        
        {/* Results info */}
        {totalElements > 0 && (
          <div className="text-center mt-3">
            <small className="text-muted">
              Showing {filteredReports.length} of {totalElements} reports
            </small>
          </div>
        )}
      </div>

      {/* Add Abstract Report Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          if (!loading) {
            clearModalFilters();
            setShowAddModal(false);
          }
        }}
        size="xl"
        centered
        backdrop={loading ? 'static' : true}
        keyboard={!loading}
        className={styles.modal}
      >
        <Form onSubmit={handleAddSubmit} className={styles.modalForm}>
          <Modal.Header closeButton={!loading} closeVariant={loading ? 'white' : undefined} className={styles.modalHeader}>
            <Modal.Title className={styles.modalTitle}>Add New Abstract Report</Modal.Title>
          </Modal.Header>
          <Modal.Body className={styles.modalBody}>
            {loading ? (
              <div className="text-center p-3">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Saving...</span>
                </Spinner>
                <p className="mt-2">Saving abstract report...</p>
              </div>
            ) : (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <FloatingLabel controlId="ashaBlockId" label={
                      <>
                        Asha Block <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Select 
                        value={formData.ashaBlockId}
                        onChange={(e) => {
                          handleInputChange(e);
                          const value = e.target.value;
                          if (value) {
                            loadAshasForAshaBlock(value);
                          } else {
                            const phcId = user.phcDetails?.id;
                            loadAshasForPhc(phcId);
                          }
                        }}
                        name="ashaBlockId"
                        required
                        style={{ height: '58px' }}
                        className="align-items-center d-flex"
                      >
                        <option value="">Select Asha Block</option>
                        {ashaBlocks.map((block) => (
                          <option key={block.id} value={block.id}>
                            {block.ashaBlock}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Please select an Asha Block
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel controlId="ashaId" label={
                      <>
                        Asha <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Select 
                        value={formData.ashaId}
                        onChange={handleInputChange}
                        name="ashaId"
                        required
                        style={{ height: '58px' }}
                        className="align-items-center d-flex"
                      >
                        <option value="">Select Asha</option>
                        {ashas.map((asha) => (
                          <option key={asha.id} value={asha.id}>
                            {asha.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Please select an Asha
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <FloatingLabel controlId="totalHouses" label={
                      <>
                        Total number of houses/ಒಟ್ಟು ಮನೆಗಳು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="totalHouses"
                        value={formData.totalHouses}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.totalHouses ? 'is-invalid' : ''}
                      />
                      {errors.totalHouses && (
                        <div className="invalid-feedback">
                          {errors.totalHouses}
                        </div>
                      )}
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel controlId="housesScreened" label={
                      <>
                        No. of houses screened/ತಪಾಸಣೆ ನಡೆಸಿದ ಮನೆಗಳು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="housesScreened"
                        value={formData.housesScreened}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.housesScreened ? 'is-invalid' : ''}
                      />
                      {errors.housesScreened && (
                        <div className="invalid-feedback">
                          {errors.housesScreened}
                        </div>
                      )}
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <FloatingLabel controlId="totalPopulation" label={
                      <>
                        Total Population/ಒಟ್ಟು ಜನಸಂಖ್ಯೆ 
                        <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="totalPopulation"
                        value={formData.totalPopulation}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.totalPopulation ? 'is-invalid' : ''}
                      />
                      {errors.totalPopulation && (
                        <div className="invalid-feedback">
                          {errors.totalPopulation}
                        </div>
                      )}
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel controlId="personsScreened" label={
                      <>
                        No. of persons screened/ಒಟ್ಟು ತಪಾಸಣೆಗೆ ಒಳಗಾದವರು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="personsScreened"
                        value={formData.personsScreened}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.personsScreened ? 'is-invalid' : ''}
                      />
                      {errors.personsScreened && (
                        <div className="invalid-feedback">
                          {errors.personsScreened}
                        </div>
                      )}
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <FloatingLabel controlId="personsAbove40Screened" label={
                      <>
                        No. of persons above 40 years screened/40 ವರ್ಷಕ್ಕಿಂತ ಮೇಲ್ಪಟ್ಟವರು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="personsAbove40Screened"
                        value={formData.personsAbove40Screened}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.personsAbove40Screened ? 'is-invalid' : ''}
                      />
                      {errors.personsAbove40Screened && (
                        <div className="invalid-feedback">
                          {errors.personsAbove40Screened}
                        </div>
                      )}
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel controlId="personsWithDiabetesScreened" label={
                      <>
                        No. of persons with Diabetes screened/ಡಯಾಬಿಟಿಸ್ ನಿಂದ ಬಳಲುತ್ತಿರುವವರು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="personsWithDiabetesScreened"
                        value={formData.personsWithDiabetesScreened}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.personsWithDiabetesScreened ? 'is-invalid' : ''}
                      />
                      {errors.personsWithDiabetesScreened && (
                        <div className="invalid-feedback">
                          {errors.personsWithDiabetesScreened}
                        </div>
                      )}
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col>
                    <FloatingLabel controlId="personsReferredToSecondaryCamp" label={
                      <>
                        No. of persons referred to Secondary camp/ದ್ವಿತೀಯ ತಪಾಸಣಾ ಶಿಬಿರಕ್ಕೆ ರೆಫರ್ ಆದವರು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="personsReferredToSecondaryCamp"
                        value={formData.personsReferredToSecondaryCamp}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.personsReferredToSecondaryCamp ? 'is-invalid' : ''}
                      />
                      {errors.personsReferredToSecondaryCamp && (
                        <div className="invalid-feedback">
                          {errors.personsReferredToSecondaryCamp}
                        </div>
                      )}
                    </FloatingLabel>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="success" 
              type="submit"
              disabled={loading}
              className={buttonStyles.primaryButton}
            >
              {loading ? 'Saving...' : 'Save Report'}
            </Button>
            <Button 
              variant="danger" 
              onClick={() => {
                clearModalFilters();
                setShowAddModal(false);
              }}
              disabled={loading}
              className={buttonStyles.dangerButton}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Abstract Report Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          if (!loading) {
            clearModalFilters();
            setShowEditModal(false);
          }
        }}
        size="xl"
        centered
        backdrop={loading ? 'static' : true}
        keyboard={!loading}
        className={styles.modal}
      >
        <Form onSubmit={handleEditSubmit} className={styles.modalForm}>
          <Modal.Header closeButton={!loading} closeVariant={loading ? 'white' : undefined} className={styles.modalHeader}>
            <Modal.Title className={styles.modalTitle}>Edit Abstract Report</Modal.Title>
          </Modal.Header>
          <Modal.Body className={styles.modalBody}>
            {loading ? (
              <div className="text-center p-3">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Updating...</span>
                </Spinner>
                <p className="mt-2">Updating abstract report...</p>
              </div>
            ) : (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <FloatingLabel controlId="editAshaBlockId" label={
                      <>
                        Asha Block <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Select 
                        value={formData.ashaBlockId}
                        onChange={(e) => {
                          handleInputChange(e);
                          const value = e.target.value;
                          if (value) {
                            loadAshasForAshaBlock(value);
                          } else {
                            const phcId = user.phcDetails?.id;
                            loadAshasForPhc(phcId);
                          }
                        }}
                        name="ashaBlockId"
                        required
                        disabled
                        style={{ height: '58px' }}
                        className="align-items-center d-flex"
                      >
                        <option value="">Select Asha Block</option>
                        {ashaBlocks.map((block) => (
                          <option key={block.id} value={block.id}>
                            {block.ashaBlock}
                          </option>
                        ))}
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel controlId="editAshaId" label={
                      <>
                        Asha <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Select 
                        value={formData.ashaId}
                        onChange={handleInputChange}
                        name="ashaId"
                        required
                        disabled
                        style={{ height: '58px' }}
                        className="align-items-center d-flex"
                      >
                        <option value="">Select Asha</option>
                        {ashas.map((asha) => (
                          <option key={asha.id} value={asha.id}>
                            {asha.name}
                          </option>
                        ))}
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <FloatingLabel controlId="editTotalHouses" label={
                      <>
                        Total number of houses/ಒಟ್ಟು ಮನೆಗಳು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="totalHouses"
                        value={formData.totalHouses}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.totalHouses ? 'is-invalid' : ''}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel controlId="editHousesScreened" label={
                      <>
                        No. of houses screened/ತಪಾಸಣೆ ನಡೆಸಿದ ಮನೆಗಳು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="housesScreened"
                        value={formData.housesScreened}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.housesScreened ? 'is-invalid' : ''}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <FloatingLabel controlId="editTotalPopulation" label={
                      <>
                        Total Population/ಒಟ್ಟು ಜನಸಂಖ್ಯೆ 
                        <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="totalPopulation"
                        value={formData.totalPopulation}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.totalPopulation ? 'is-invalid' : ''}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel controlId="editPersonsScreened" label={
                      <>
                        No. of persons screened/ಒಟ್ಟು ತಪಾಸಣೆಗೆ ಒಳಗಾದವರು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="personsScreened"
                        value={formData.personsScreened}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.personsScreened ? 'is-invalid' : ''}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <FloatingLabel controlId="editPersonsAbove40Screened" label={
                      <>
                        No. of persons above 40 years screened/40 ವರ್ಷಕ್ಕಿಂತ ಮೇಲ್ಪಟ್ಟವರು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="personsAbove40Screened"
                        value={formData.personsAbove40Screened}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.personsAbove40Screened ? 'is-invalid' : ''}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel controlId="editPersonsWithDiabetesScreened" label={
                      <>
                        No. of persons with Diabetes screened/ಡಯಾಬಿಟಿಸ್ ನಿಂದ ಬಳಲುತ್ತಿರುವವರು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="personsWithDiabetesScreened"
                        value={formData.personsWithDiabetesScreened}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.personsWithDiabetesScreened ? 'is-invalid' : ''}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col>
                    <FloatingLabel controlId="editPersonsReferredToSecondaryCamp" label={
                      <>
                        No. of persons referred to Secondary camp/ದ್ವಿತೀಯ ತಪಾಸಣಾ ಶಿಬಿರಕ್ಕೆ ರೆಫರ್ ಆದವರು <span className="text-danger">*</span>
                      </>
                    }>
                      <Form.Control 
                        type="number" 
                        name="personsReferredToSecondaryCamp"
                        value={formData.personsReferredToSecondaryCamp}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="0"
                        required
                        className={errors.personsReferredToSecondaryCamp ? 'is-invalid' : ''}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="success" 
              type="submit"
              disabled={loading}
              className={buttonStyles.primaryButton}
            >
              {loading ? 'Updating...' : 'Update Report'}
            </Button>
            <Button 
              variant="danger" 
              onClick={() => {
                clearModalFilters();
                setShowEditModal(false);
              }}
              disabled={loading}
              className={buttonStyles.dangerButton}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={modalData.title}
        message={modalData.message}
        variant={modalData.variant || 'info'}
      />
    </div>
  );
}

export default AshaAbstractReport;
